import { userLabel, userIdentityEl } from './user-identity.js';
/**
 * MailPlug풍 휴가/근태 신청 모달.
 * - 카테고리(leave|work)에 맞는 종류만 노출, 반차 라디오는 반차 허용 유형만
 * - 커스텀 데이트피커 (달력 팝오버, 오늘/선택 강조, 주말 색)
 * - 결재자/참조자: 이름·직급·부서 검색 피커로 선택, 카드로 표시(이름이 메인)
 * - 마지막 결재라인은 localStorage에 저장해 다음 신청에 프리필
 * - "계속 신청하기"는 제출 후 모달을 다시 연다(서버 reopen 리다이렉트)
 */
import { attachDatePicker, closeDatePicker, isDatePickerOpen, syncDateLabel as dpSyncLabel } from './datepicker.js';
import { pagePrefs, savePref } from './saved-views.js';

const LINE_KEY = 'attReqLine';
// 서버 저장 결재선 (AO1): user_preferences 의 attendance.line — 브라우저를 바꿔도 따라온다.
const LINE_PREF = 'attendance.line';

export function setupAttendanceRequestModal() {
    const modal = document.getElementById('attReqModal');
    if (!modal || modal.dataset.ready) return;
    modal.dataset.ready = "1";

    const form = document.getElementById('attReqForm');
    const title = document.getElementById('attReqModalTitle');
    const typeSel = document.getElementById('attReqType');
    const catInput = document.getElementById('attReqCategory');
    const stayInput = document.getElementById('attReqStay');
    const dayPartRow = document.getElementById('attReqDayPartRow');
    const leaveLine = document.getElementById('attReqLeaveLine');
    const startEl = document.getElementById('attReqStart');
    const endEl = document.getElementById('attReqEnd');
    const daysBadge = document.getElementById('attReqDays');

    // ── 인원 데이터 ──
    const people = Array.from(document.querySelectorAll('#attReqPeople span')).map((el) => ({
        id: el.dataset.id,
        name: el.dataset.name,
        title: el.dataset.title || '',
        dept: el.dataset.dept || '',
        username: el.dataset.username || '',
    }));
    const byID = {};
    people.forEach((p) => {
        byID[p.id] = p;
    });

    function personCard(p, onRemove) {
        const card = document.createElement('div');
        card.className = 'att-person-card';
        const avatar = document.createElement('span');
        avatar.className = 'att-person-avatar';
        avatar.textContent = p.name.charAt(0);
        const main = document.createElement('span');
        main.className = 'att-person-main';
        const nameEl = document.createElement('strong');
        nameEl.replaceChildren(userIdentityEl(p));
        if (p.title) nameEl.appendChild(document.createTextNode(' ' + p.title));
        const sub = document.createElement('span');
        sub.className = 'att-person-sub';
        sub.textContent = p.dept || '';
        main.appendChild(nameEl);
        main.appendChild(sub);
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'att-person-remove';
        rm.textContent = '✕';
        rm.addEventListener('click', onRemove);
        card.appendChild(avatar);
        card.appendChild(main);
        card.appendChild(rm);
        return card;
    }

    // ── 결재선 (순차, 1차 → 2차 …) ──
    const lineList = document.getElementById('attReqLineList');
    const approverEmpty = document.getElementById('attReqApproverEmpty');
    // 추천 결재자 = 서버가 넘긴 부서장(상위 부서로 올라가며 첫 리더). 결재선이 비어
    // 있고 저장된 결재선도 없으면 1차로 자동 채우고, 비어 있으면 「＋ 이름 (부서장 추천)」
    // 버튼으로 남는다.
    const suggestedID = modal.dataset.suggested && byID[modal.dataset.suggested] ? modal.dataset.suggested : '';
    const suggestBtn = document.getElementById('attReqSuggestBtn');
    let approverIDs = [];
    function renderLine() {
        lineList.innerHTML = '';
        form.querySelectorAll('input[name="approver_ids"]').forEach((el) => el.remove());
        approverIDs.forEach((id, i) => {
            const p = byID[id];
            if (!p) return;
            const card = personCard(p, () => {
                approverIDs = approverIDs.filter((x) => x !== id);
                renderLine();
            });
            const badge = document.createElement('span');
            badge.className = 'att-step-badge';
            badge.textContent = (i + 1) + (modal.dataset.stepSuffix || '차');
            card.insertBefore(badge, card.firstChild);
            if (id === suggestedID) {
                const tag = document.createElement('span');
                tag.className = 'att-suggest-tag';
                tag.textContent = modal.dataset.suggestedTag || '부서장 추천';
                card.insertBefore(tag, card.lastChild);
            }
            lineList.appendChild(card);
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'approver_ids';
            hidden.value = id;
            form.appendChild(hidden);
        });
        approverEmpty.style.display = approverIDs.length ? 'none' : 'block';
        if (suggestBtn) {
            const show = !!suggestedID && approverIDs.indexOf(suggestedID) < 0 && ccIDs.indexOf(suggestedID) < 0;
            suggestBtn.hidden = !show;
            if (show) {
                const p = byID[suggestedID];
                const nameEl = suggestBtn.querySelector('[data-suggest-name]');
                if (nameEl) nameEl.textContent = userLabel(p) + (p.title ? ' ' + p.title : '');
            }
        }
        // 「최근 결재선」: 저장된 결재선이 있고 지금 결재선이 비었을 때만 (AO1).
        if (recentBtn) {
            const line = approverIDs.length ? null : savedLine();
            recentBtn.hidden = !line;
            if (line) {
                const names = line.approvers.map((id) => userLabel(byID[id])).join(' → ');
                const el = recentBtn.querySelector('[data-recent-line]');
                if (el) el.textContent = names;
            }
        }
    }
    if (suggestBtn) {
        suggestBtn.addEventListener('click', () => {
            if (!suggestedID || approverIDs.indexOf(suggestedID) >= 0) return;
            approverIDs.push(suggestedID);
            renderLine();
        });
    }

    // ── 참조자 (N인) ──
    const ccList = document.getElementById('attReqCCList');
    let ccIDs = [];
    function renderCCs() {
        ccList.innerHTML = '';
        form.querySelectorAll('input[name="cc_ids"]').forEach((el) => el.remove());
        ccIDs.forEach((id) => {
            const p = byID[id];
            if (!p) return;
            ccList.appendChild(personCard(p, () => {
                ccIDs = ccIDs.filter((x) => x !== id);
                renderCCs();
            }));
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'cc_ids';
            hidden.value = id;
            form.appendChild(hidden);
        });
    }

    // ── 인원 검색 피커 ──
    let picker = null;
    function closePicker() {
        if (picker) {
            picker.remove();
            picker = null;
        }
    }
    function openPicker(anchor, exclude, onPick) {
        closePicker();
        const box = document.createElement('div');
        box.className = 'att-picker';
        const search = document.createElement('input');
        search.type = 'text';
        search.placeholder = modal.dataset.searchPh || '이름·부서·직급 검색';
        const list = document.createElement('div');
        list.className = 'att-picker-list';
        box.appendChild(search);
        box.appendChild(list);

        function render(q) {
            list.innerHTML = '';
            const needle = (q || '').toLowerCase();
            const matches = people.filter((p) => {
                if (exclude.indexOf(p.id) >= 0) return false;
                if (!needle) return true;
                return (p.name + ' ' + p.dept + ' ' + p.title + ' ' + p.username).toLowerCase().indexOf(needle) >= 0;
            });
            if (!matches.length) {
                const empty = document.createElement('div');
                empty.className = 'att-picker-empty text-muted';
                empty.textContent = modal.dataset.searchEmpty || '검색 결과가 없습니다';
                list.appendChild(empty);
                return;
            }
            matches.slice(0, 50).forEach((p, i) => {
                const row = document.createElement('button');
                row.type = 'button';
                row.className = 'att-picker-row' + (i === 0 ? ' hl' : '');
                const avatar = document.createElement('span');
                avatar.className = 'att-person-avatar';
                avatar.textContent = p.name.charAt(0);
                const main = document.createElement('span');
                main.className = 'att-person-main';
                const nameEl = document.createElement('strong');
                nameEl.replaceChildren(userIdentityEl(p));
                if (p.title) nameEl.appendChild(document.createTextNode(' ' + p.title));
                const sub = document.createElement('span');
                sub.className = 'att-person-sub';
                sub.textContent = p.dept || '';
                main.appendChild(nameEl);
                main.appendChild(sub);
                row.appendChild(avatar);
                row.appendChild(main);
                row.addEventListener('click', () => {
                    closePicker();
                    onPick(p.id);
                });
                list.appendChild(row);
            });
        }
        render('');
        search.addEventListener('input', () => render(search.value));
        search.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const first = list.querySelector('.att-picker-row');
                if (first) first.click();
            } else if (e.key === 'Escape') {
                // 피커만 닫는다. 이 keydown 이 document 까지 올라가면 그쪽 핸들러가
                // "피커 없음" 으로 보고 모달 전체를 닫아 입력을 잃었다(2026-09-18).
                e.stopPropagation();
                closePicker();
                anchor.focus({ preventScroll: true });
            }
        });

        anchor.parentElement.style.position = 'relative';
        anchor.parentElement.appendChild(box);
        picker = box;
        search.focus();
    }
    document.addEventListener('click', (e) => {
        if (picker && !picker.contains(e.target) && !e.target.closest('#attReqPickApprover') && !e.target.closest('#attReqPickCC')) {
            closePicker();
        }
    });

    document.getElementById('attReqPickApprover').addEventListener('click', function () {
        if (picker && picker.dataset.owner === 'approver') { closePicker(); return; }
        openPicker(this, approverIDs.concat(ccIDs), (id) => {
            approverIDs.push(id);
            renderLine();
        });
        if (picker) picker.dataset.owner = 'approver';
    });
    document.getElementById('attReqPickCC').addEventListener('click', function () {
        if (picker && picker.dataset.owner === 'cc') { closePicker(); return; }
        openPicker(this, ccIDs.concat(approverIDs), (id) => {
            ccIDs.push(id);
            renderCCs();
            renderLine(); // 추천 버튼은 참조자로 고른 사람도 뺀다
        });
        if (picker) picker.dataset.owner = 'cc';
    });

    // ── 커스텀 데이트피커 (공용 모듈: datepicker.js) ──
    function fmt(y, m, d) {
        return y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
    }
    function syncDateLabel(btn, hidden) {
        dpSyncLabel(btn, hidden, modal.dataset.pickDate || '날짜 선택');
    }
    const startBtn = document.getElementById('attReqStartBtn');
    const endBtn = document.getElementById('attReqEndBtn');
    const dpBase = {
        weekdays: modal.dataset.weekdays,
        placeholder: modal.dataset.pickDate || '날짜 선택',
        onOpen: closePicker,
    };
    attachDatePicker(startBtn, startEl, Object.assign({}, dpBase, { onSet: () => {
        if (!endEl.value || endEl.value < startEl.value) {
            endEl.value = startEl.value;
            syncDateLabel(endBtn, endEl);
        }
        updateDays();
    } }));
    attachDatePicker(endBtn, endEl, Object.assign({}, dpBase, { onSet: updateDays }));

    // ── 종류/반차·반반차/일수 ──
    // 단위 선택은 [종일|반차|반반차] 세그먼트 + (반차/반반차일 때) 슬롯
    // 세그먼트의 2단 구성. 조합 결과만 hidden day_part 하나에 기록한다:
    // "" | am | pm | am1 | am2 | pm1 | pm2.
    const dayPartInput = document.getElementById('attReqDayPart');
    const slotsHalf = document.getElementById('attReqSlotsHalf');
    const slotsQuarter = document.getElementById('attReqSlotsQuarter');
    const slotHint = document.getElementById('attReqSlotHint');
    let dayMode = 'full'; // full | half | quarter

    function activeSlot(container) {
        const btn = container.querySelector('.att-seg-btn.active');
        return btn ? btn.dataset.slot : '';
    }
    function syncDayPartValue() {
        if (dayMode === 'half') dayPartInput.value = activeSlot(slotsHalf);
        else if (dayMode === 'quarter') dayPartInput.value = activeSlot(slotsQuarter);
        else dayPartInput.value = '';
        if (slotHint) {
            slotHint.style.display = dayMode === 'quarter' ? '' : 'none';
            slotHint.textContent = dayMode === 'quarter' ? (modal.dataset.quarterHint || '') : '';
        }
        updateDays();
    }
    function setDayMode(mode) {
        dayMode = mode;
        dayPartRow.querySelectorAll('.att-seg-btn[data-mode]').forEach((b) => {
            b.classList.toggle('active', b.dataset.mode === mode);
        });
        slotsHalf.style.display = mode === 'half' ? 'inline-flex' : 'none';
        slotsQuarter.style.display = mode === 'quarter' ? 'inline-flex' : 'none';
        syncDayPartValue();
    }
    dayPartRow.querySelectorAll('.att-seg-btn[data-mode]').forEach((b) => {
        b.addEventListener('click', () => setDayMode(b.dataset.mode));
    });
    [slotsHalf, slotsQuarter].forEach((container) => {
        container.querySelectorAll('.att-seg-btn[data-slot]').forEach((b) => {
            b.addEventListener('click', () => {
                container.querySelectorAll('.att-seg-btn').forEach((x) => x.classList.toggle('active', x === b));
                syncDayPartValue();
            });
        });
    });

    function selectedType() {
        return typeSel.options[typeSel.selectedIndex] || null;
    }
    function syncDayPart() {
        const opt = selectedType();
        const halfable = opt && opt.dataset.half === '1';
        dayPartRow.style.display = halfable ? '' : 'none';
        if (!halfable) setDayMode('full');
        else updateDays();
    }
    function filterTypes(category) {
        let first = null;
        Array.from(typeSel.options).forEach((opt) => {
            const show = opt.dataset.category === category;
            opt.hidden = !show;
            opt.disabled = !show;
            if (show && !first) first = opt;
        });
        if (first) typeSel.value = first.value;
        leaveLine.style.display = category === 'leave' ? '' : 'none';
        syncDayPart();
    }
    function halfChecked() {
        return dayPartInput.value !== '';
    }
    const endWrap = document.getElementById('attReqEndWrap');
    const holidays = new Set((modal.dataset.holidays || '').split(',').filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)));
    function updateDays() {
        const half = halfChecked();
        if (endWrap) endWrap.style.display = half ? 'none' : 'contents';
        if (half && startEl.value) {
            endEl.value = startEl.value;
            syncDateLabel(endBtn, endEl);
        }
        daysBadge.textContent = '';
        daysBadge.classList.remove('att-days-zero');
        daysBadge.removeAttribute('title');
        if (!startEl.value || !endEl.value) return;
        const start = new Date(startEl.value + 'T00:00:00');
        const end = new Date(endEl.value + 'T00:00:00');
        if (isNaN(start) || isNaN(end) || end < start) return;
        if (half) {
            // Half = 4h, quarter = 2h of an 8h day — mirrors the server's "Xd Yh".
            daysBadge.textContent = '총 ' + (dayMode === 'quarter' ? '2h' : '4h');
            return;
        }
        let n = 0;
        const d = new Date(start);
        while (d <= end) {
            const wd = d.getDay();
            // 공휴일(data-holidays, 서버의 holidays 표)도 뺀다 — 서버의 예상 차감과 같은 셈 (AO8).
            if (wd !== 0 && wd !== 6 && !holidays.has(fmt(d.getFullYear(), d.getMonth(), d.getDate()))) n += 1;
            d.setDate(d.getDate() + 1);
        }
        daysBadge.textContent = '총 ' + n + 'd';
        if (n === 0) {
            // 주말만 고른 기간 — 서버는 어차피 0d 로 거절하니 미리 알린다.
            daysBadge.classList.add('att-days-zero');
            daysBadge.title = modal.dataset.daysZeroHint || '';
        }
    }

    // ── 결재라인 저장/프리필 ──
    // 저장은 서버(user_preferences attendance.line)와 localStorage 둘 다; 읽기는 서버가 먼저다.
    function saveLine() {
        const doc = JSON.stringify({ approvers: approverIDs, ccs: ccIDs });
        try { localStorage.setItem(LINE_KEY, doc); } catch (e) { /* 무해 */ }
        try { savePref(LINE_PREF, doc).catch(() => {}); } catch (e) { /* 무해 */ }
    }
    // savedLine reads the stored line (server pref, else localStorage) filtered to
    // people the picker knows; null when nothing usable is stored.
    function savedLine() {
        let raw = '';
        try { raw = pagePrefs()[LINE_PREF] || ''; } catch (e) { raw = ''; }
        if (!raw) {
            try { raw = localStorage.getItem(LINE_KEY) || ''; } catch (e) { raw = ''; }
        }
        if (!raw) return null;
        try {
            const saved = JSON.parse(raw);
            let line = Array.isArray(saved.approvers) ? saved.approvers : [];
            if (!line.length && saved.approver) line = [saved.approver]; // 구버전 호환
            const approvers = line.filter((id) => byID[id]);
            const ccs = (Array.isArray(saved.ccs) ? saved.ccs : []).filter((id) => byID[id] && approvers.indexOf(id) < 0);
            return approvers.length ? { approvers, ccs } : null;
        } catch (e) {
            return null;
        }
    }
    const recentBtn = document.getElementById('attReqRecentBtn');
    function applySavedLine(line) {
        approverIDs = line.approvers.slice();
        ccIDs = line.ccs.filter((id) => approverIDs.indexOf(id) < 0);
        renderLine();
        renderCCs();
    }
    if (recentBtn) {
        recentBtn.addEventListener('click', () => {
            const line = savedLine();
            if (line) applySavedLine(line);
        });
    }
    function prefillLine() {
        if (approverIDs.length || ccIDs.length) return;
        const line = savedLine();
        if (line) {
            applySavedLine(line);
            return;
        }
        // 저장된 결재선이 없으면 부서장을 1차로 — 첫 신청부터 사람을 찾지 않아도 된다.
        if (suggestedID && ccIDs.indexOf(suggestedID) < 0) approverIDs = [suggestedID];
        renderLine();
        renderCCs();
    }

    // ── 모달 열기/닫기 ──
    function open(category) {
        catInput.value = category;
        title.textContent = category === 'work' ? modal.dataset.titleWork : modal.dataset.titleLeave;
        setDayMode('full'); // a fresh request always starts as a full day
        filterTypes(category);
        if (!startEl.value) {
            // 다음 근무일 — 서버가 주말·공휴일을 뺀 날짜(data-default-date)를 준다; 없으면 내일.
            let def = modal.dataset.defaultDate || '';
            if (!/^\d{4}-\d{2}-\d{2}$/.test(def)) {
                const t = new Date();
                t.setDate(t.getDate() + 1);
                def = fmt(t.getFullYear(), t.getMonth(), t.getDate());
            }
            startEl.value = def;
            endEl.value = def;
        }
        syncDateLabel(startBtn, startEl);
        syncDateLabel(endBtn, endEl);
        prefillLine();
        updateDays();
        modal.style.display = 'flex';
        document.body.classList.add('modal-scroll-lock');
        typeSel.focus();
    }
    function close() {
        closePicker();
        closeDatePicker();
        modal.style.display = 'none';
        document.body.classList.remove('modal-scroll-lock');
    }

    document.querySelectorAll('[data-open-request]').forEach((el) => {
        el.addEventListener('click', (e) => {
            e.preventDefault();
            open(el.dataset.openRequest === 'work' ? 'work' : 'leave');
        });
    });
    document.getElementById('attReqClose').addEventListener('click', close);
    // 입력 폼 모달은 바깥 클릭으로 닫지 않는다 (실수로 작성 내용 유실 방지).
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || modal.style.display === 'none') return;
        if (picker) closePicker();
        else if (isDatePickerOpen()) closeDatePicker();
        else close();
    });

    typeSel.addEventListener('change', syncDayPart);

    // ── 제출 (숨은 필드는 직접 검증) ──
    function validate() {
        if (!startEl.value || !endEl.value) {
            startBtn.click();
            return false;
        }
        if (!approverIDs.length) {
            if (window.showNotification) window.showNotification(modal.dataset.approverRequired || '결재자를 선택해주세요', 'error');
            document.getElementById('attReqPickApprover').click();
            return false;
        }
        return true;
    }
    let submitting = false;
    const lockSubmission = () => { if (submitting) return false; submitting = true; form.querySelectorAll("button").forEach(b => b.disabled = true); return true; };
    document.getElementById('attReqSubmitStay').addEventListener('click', () => {
        if (!validate() || !form.reportValidity() || !lockSubmission()) return;
        stayInput.value = '1';
        saveLine();
        form.submit();
    });
    form.addEventListener('submit', (e) => {
        if (!validate() || !lockSubmission()) {
            e.preventDefault();
            return;
        }
        saveLine();
    });

    // ── 신청 결과 토스트 + 모달 재오픈 (서버 reopen 리다이렉트) ──
    const flash = document.getElementById('attendance-req-flash');
    if (flash && flash.dataset.message && !flash.dataset.shown && window.showNotification) {
        flash.dataset.shown = '1'; // 결재 모듈(attendance-approval.js)도 같은 아일랜드를 본다
        window.showNotification(flash.dataset.message, flash.dataset.error === '1' ? 'error' : 'success');
    }
    // 팀 캘린더 셀에서 온 ?date= 프리필 (spec 2026-09-07 §7.3): open() 의 "내일"
    // 기본값보다 먼저 채운다. 사용자가 이미 고른 날짜는 덮어쓰지 않는다.
    // ?reopen= 없이 ?date= 만 온 주소는 모달을 열지 않으므로 프리필도 하지 않는다 —
    // 안 그러면 숨은 입력만 조용히 채워져 나중에 손으로 연 신청서가 그 날짜로 뜬다.
    const prefillKind = modal.dataset.reopen;
    if (modal.dataset.date && (prefillKind === 'leave' || prefillKind === 'work')) {
        const preStart = document.getElementById('attReqStart'), preEnd = document.getElementById('attReqEnd');
        if (preStart && !preStart.value) {
            preStart.value = modal.dataset.date;
            if (preEnd) preEnd.value = modal.dataset.date;
        }
    }
    const reopen = modal.dataset.reopen;
    if (reopen === 'leave' || reopen === 'work') open(reopen);
    const url = new URL(window.location.href);
    if (url.searchParams.has('reopen') || url.searchParams.has('success') || url.searchParams.has('error') || url.searchParams.has('date')) {
        ['reopen', 'success', 'error', 'date'].forEach((k) => url.searchParams.delete(k));
        window.history.replaceState({}, '', url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
    }
}
